import type { MouseEventHandler } from "react";

import {
  Button,
  CustomLink,
  Icon,
  Tag,
  Typo,
} from "@pepperstone/pepper-ui-kit";

import { PepperstoneIcon } from "../Icons";
import { flagsMap } from "./flagMapping";

export type TradingCardProps = {
  accountLabel?: string;
  accountType: string;
  accountId: string;
  accountName: string;
  currency: string;
  balanceLabel: string;
  balanceValue: string;
  primaryLinkLabel: string;
  primaryLinkHref: string;
  onClickPrimaryLink?: MouseEventHandler;
  secondaryLinkLabel: string;
  secondaryLinkHref: string;
  onClickSecondaryLink?: MouseEventHandler;
  settingsLinkHref: string;
  onClickSettingsLink?: MouseEventHandler;
  disableRebateLinkHref?: boolean;
};

export const TradingCard = ({
  accountLabel,
  accountType,
  accountId,
  accountName,
  currency,
  balanceLabel,
  balanceValue,
  primaryLinkLabel,
  primaryLinkHref,
  onClickPrimaryLink,
  secondaryLinkLabel,
  secondaryLinkHref,
  onClickSecondaryLink,
  settingsLinkHref,
  onClickSettingsLink,
  disableRebateLinkHref,
}: TradingCardProps) => {
  const Flag = flagsMap.get(currency);

  return (
    <div className="flex min-h-72 w-full flex-col gap-4 rounded-2xl bg-surface-emphasis p-4">
      <div className="flex grow flex-col gap-2">
        <div className="flex items-center gap-2 pb-2">
          {accountLabel && (
            <Tag className="pointer-events-none" label={accountLabel} />
          )}
          <div className="flex grow items-center justify-between gap-4">
            <div className="flex flex-1 basis-7/12 items-center gap-1">
              <Typo
                elem="p"
                type="label"
                size="xs-mobile"
                textColor="ps-text-text-onemphasis-base"
                classes="ps-line-clamp-1 ps-break-words"
              >
                {accountType}
              </Typo>
            </div>
            <div className="ms-auto flex basis-5/12 justify-end overflow-hidden">
              <Typo
                elem="p"
                type="label"
                size="xs-mobile"
                textColor="ps-text-text-onemphasis-secondary"
              >
                {accountId}
              </Typo>
            </div>
          </div>
        </div>
        <div className="grow rounded-lg bg-blue-800 p-4">
          <div className="flex items-start justify-between py-2">
            <div className="flex grow flex-col gap-1">
              <Typo
                elem="p"
                type="label"
                size="md-mobile"
                textColor="ps-text-text-onemphasis-base"
                classes="!font-bold"
              >
                {accountName}
              </Typo>
            </div>
            <div>
              <PepperstoneIcon />
            </div>
          </div>
        </div>
      </div>
      <div className="space-y-xs">
        <div className="flex items-center justify-between gap-2">
          <div className="shrink-0 select-none">
            {/* oxlint-disable-next-line react/static-components -- Flag comes from the module-level flagsMap, so its identity is stable */}
            {Flag && <Flag data-testid={`flag-${currency}`} className="w-8" />}
          </div>
          <div className="flex grow flex-col">
            <Typo
              elem="p"
              type="caption"
              size="sm"
              textColor="ps-text-text-onemphasis-secondary"
            >
              {balanceLabel}
            </Typo>
            <Typo
              elem="p"
              type="label"
              size="md"
              textColor="ps-text-text-onemphasis-base"
            >
              {balanceValue}
            </Typo>
          </div>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div
            className={`grid grow grid-cols-2 gap-2 ${
              disableRebateLinkHref ? "opacity-50" : ""
            }`}
          >
            <Button
              as="a"
              type="button"
              variant="ps-btn-primary"
              href={disableRebateLinkHref ? undefined : primaryLinkHref}
              target="_blank"
              onClick={disableRebateLinkHref ? undefined : onClickPrimaryLink}
              disabled={disableRebateLinkHref}
              aria-disabled={disableRebateLinkHref}
            >
              {primaryLinkLabel}
            </Button>
            <Button
              as="a"
              type="button"
              variant="ps-btn-secondary"
              theme="dark"
              href={disableRebateLinkHref ? undefined : secondaryLinkHref}
              target="_blank"
              onClick={disableRebateLinkHref ? undefined : onClickSecondaryLink}
              disabled={disableRebateLinkHref}
              aria-disabled={disableRebateLinkHref}
            >
              {secondaryLinkLabel}
            </Button>
          </div>
          <CustomLink
            onClick={onClickSettingsLink}
            href={settingsLinkHref}
            className="shrink-0"
          >
            <Icon color="white" icon="settings" size="medium" />
          </CustomLink>
        </div>
      </div>
    </div>
  );
};
